import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, X, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { isoDate, parseIsoDate, MONTHS_TR } from "@/lib/calendarUtils";

// Module-level multi-day picker: click calendar icon -> pick many days at once -> "Ekle".
function MultiDatePicker({ dates, setDates, testid }) {
  const [open, setOpen] = useState(false);
  const [temp, setTemp] = useState([]);

  // When popover opens, seed the temp selection with already-added days.
  useEffect(() => {
    if (open) setTemp((dates || []).map(parseIsoDate));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    const merged = Array.from(new Set([...(dates || []), ...temp.map(isoDate)])).sort();
    setDates(merged);
    setOpen(false);
  };

  const removeDate = (d) => setDates((dates || []).filter((x) => x !== d));

  const fmt = (iso) => {
    const dt = parseIsoDate(iso);
    return `${dt.getDate()} ${MONTHS_TR[dt.getMonth()].slice(0, 3)}`;
  };

  return (
    <div>
      <Label>Planlanan Günler</Label>
      <div className="mt-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" data-testid={`${testid}-open`}>
              <CalendarPlus size={14} className="mr-1.5" />
              Takvimden gün seç
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="multiple"
              selected={temp}
              onSelect={(days) => setTemp(days || [])}
              weekStartsOn={1}
            />
            <div className="flex items-center justify-between border-t px-3 py-2">
              <span className="text-xs text-zinc-500" data-testid={`${testid}-count`}>
                {temp.length} gün seçildi
              </span>
              <div className="flex gap-1.5">
                <Button type="button" size="sm" variant="ghost" onClick={() => setTemp([])}>
                  Temizle
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-zinc-900 hover:bg-zinc-800"
                  onClick={commit}
                  data-testid={`${testid}-commit`}
                >
                  Ekle
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {(dates || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testid}-chips`}>
          {dates.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded border border-zinc-200 bg-zinc-50 text-xs">
              {fmt(d)}
              <button type="button" onClick={() => removeDate(d)} className="text-zinc-500 hover:text-red-600 p-0.5" aria-label="Kaldır">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Dialog for creating (or editing) a calendar entry.
export default function CalendarJobDialog({ open, onOpenChange, onSaved, editEntry, defaultDate }) {
  const isEdit = !!editEntry;
  const editKind = editEntry?.kind || "assignment";

  const [tab, setTab] = useState(editKind);
  const [saving, setSaving] = useState(false);

  // shared
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);

  // assignment fields
  const [aProjectId, setAProjectId] = useState("");
  const [aItemId, setAItemId] = useState("");
  const [aStage, setAStage] = useState("");
  const [aUserIds, setAUserIds] = useState([]); // çoklu usta; boş = atanmamış
  const [aPriority, setAPriority] = useState("normal");
  const [aDates, setADates] = useState([]);
  const [aNotes, setANotes] = useState("");

  // free-job fields
  const [fTitle, setFTitle] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [fUserId, setFUserId] = useState("");
  const [fPriority, setFPriority] = useState("normal");
  const [fDates, setFDates] = useState([]);

  const resetAll = () => {
    setAProjectId(""); setAItemId(""); setAStage(""); setAUserIds([]);
    setAPriority("normal"); setADates(defaultDate ? [defaultDate] : []); setANotes("");
    setFTitle(""); setFDesc(""); setFUserId(""); setFPriority("normal");
    setFDates(defaultDate ? [defaultDate] : []);
    setTab(editEntry ? (editEntry.kind || "assignment") : "assignment");
  };

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/users").then(r => setUsers(r.data)).catch(() => setUsers([])),
      api.get("/projects", { params: { status: "active" } }).then(r => setProjects(r.data)).catch(() => setProjects([])),
      api.get("/settings").then(r => setStages(r.data.manufacturing_stages || [])).catch(() => setStages([])),
    ]);
    if (editEntry) {
      setTab(editEntry.kind || "assignment");
      if ((editEntry.kind || "assignment") === "assignment") {
        setAProjectId(editEntry.project_id || "");
        setAItemId(editEntry.item_id || "");
        setAStage(editEntry.stage || "");
        setAUserIds(editEntry.user_id ? [editEntry.user_id] : []);
        setAPriority(editEntry.priority || "normal");
        setADates(editEntry.scheduled_dates || []);
        setANotes(editEntry.notes || "");
      } else {
        setFTitle(editEntry.title || "");
        setFDesc(editEntry.notes || "");
        setFUserId(editEntry.user_id || "");
        setFPriority(editEntry.priority || "normal");
        setFDates(editEntry.scheduled_dates || []);
      }
    } else {
      resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editEntry?.id]);

  useEffect(() => {
    if (!aProjectId) { setItems([]); return; }
    api.get(`/projects/${aProjectId}/items`).then(r => setItems(r.data)).catch(() => setItems([]));
  }, [aProjectId]);

  const ustas = useMemo(() => users.filter(u => u.role === "usta"), [users]);
  const allUsers = users;
  const ustaList = ustas.length ? ustas : allUsers;

  const toggleUsta = (id) =>
    setAUserIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const saveAssignment = async () => {
    if (!aProjectId) { toast.error("Proje seçin"); return; }
    if (!aItemId) { toast.error("Ürün seçin"); return; }
    if (!aStage) { toast.error("Aşama seçin"); return; }
    if (!aDates.length) { toast.error("En az bir tarih ekleyin"); return; }
    setSaving(true);
    try {
      if (isEdit && editEntry.kind === "assignment") {
        const body = {
          stage: aStage,
          priority: aPriority,
          scheduled_dates: aDates,
          notes: aNotes,
        };
        if (aUserIds.length) body.user_id = aUserIds[0];
        else body.clear_user = true;
        const r = await api.put(`/projects/${aProjectId}/items/${aItemId}/assignments/${editEntry.id}`, body);
        toast.success("Görev güncellendi");
        onSaved?.(r.data);
      } else {
        // Usta seçilmezse tek bir atanmamış görev; birden çok usta için usta başına birer görev.
        const targets = aUserIds.length ? aUserIds : [null];
        let last = null;
        for (const uid of targets) {
          const r = await api.post(`/projects/${aProjectId}/items/${aItemId}/assignments`, {
            user_id: uid,
            stage: aStage,
            priority: aPriority,
            scheduled_dates: aDates,
            notes: aNotes,
          });
          last = r.data;
        }
        toast.success(targets.length > 1 ? `${targets.length} görev oluşturuldu` : "Görev oluşturuldu");
        onSaved?.(last);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const saveFree = async () => {
    if (!fTitle.trim()) { toast.error("Başlık zorunlu"); return; }
    if (!fDates.length) { toast.error("En az bir tarih ekleyin"); return; }
    setSaving(true);
    try {
      const body = {
        title: fTitle.trim(),
        description: fDesc,
        scheduled_dates: fDates,
        user_id: fUserId || null,
        priority: fPriority,
        status: "pending",
      };
      let saved;
      if (isEdit && editEntry.kind === "free") {
        const patch = { ...body };
        if (!fUserId) { patch.clear_user = true; delete patch.user_id; }
        const r = await api.put(`/calendar/jobs/${editEntry.id}`, patch);
        saved = r.data;
      } else {
        const r = await api.post(`/calendar/jobs`, body);
        saved = r.data;
      }
      toast.success(isEdit ? "İş güncellendi" : "İş oluşturuldu");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!editEntry) return;
    try {
      if (editEntry.kind === "free") {
        await api.delete(`/calendar/jobs/${editEntry.id}`);
      } else if (editEntry.kind === "assignment") {
        await api.delete(`/projects/${editEntry.project_id}/items/${editEntry.item_id}/assignments/${editEntry.id}`);
      }
      toast.success("Silindi");
      onSaved?.(null);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="calendar-job-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Planlamayı Düzenle" : "Yeni Planlama"}</DialogTitle>
          <DialogDescription>Bir üretim görevi atayın veya proje/ürün'e bağlı olmayan serbest bir iş ekleyin.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={(v) => !isEdit && setTab(v)}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="assignment" disabled={isEdit && editKind !== "assignment"} data-testid="tab-assignment">Proje Görevi</TabsTrigger>
            <TabsTrigger value="free" disabled={isEdit && editKind !== "free"} data-testid="tab-free">Serbest İş</TabsTrigger>
          </TabsList>

          <TabsContent value="assignment" className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Proje</Label>
                <Select value={aProjectId} onValueChange={setAProjectId} disabled={isEdit}>
                  <SelectTrigger data-testid="calendar-project-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Ürün</Label>
                <Select value={aItemId} onValueChange={setAItemId} disabled={isEdit || !aProjectId}>
                  <SelectTrigger data-testid="calendar-item-select"><SelectValue placeholder={aProjectId ? "Seçin" : "Önce proje seçin"} /></SelectTrigger>
                  <SelectContent>{items.map(it => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Aşama</Label>
                <Select value={aStage} onValueChange={setAStage}>
                  <SelectTrigger data-testid="calendar-stage-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>{stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Öncelik</Label>
                <Select value={aPriority} onValueChange={setAPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Usta{isEdit ? "" : " (opsiyonel, birden fazla seçebilirsiniz)"}</Label>
                {isEdit ? (
                  <Select
                    value={aUserIds[0] || "__none"}
                    onValueChange={(v) => setAUserIds(v === "__none" ? [] : [v])}
                  >
                    <SelectTrigger data-testid="calendar-user-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Atanmamış (genel)</SelectItem>
                      {ustaList.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="mt-1 max-h-32 overflow-y-auto rounded-md border border-zinc-200 divide-y" data-testid="calendar-usta-list">
                    {ustaList.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">Usta bulunamadı</div>}
                    {ustaList.map(u => (
                      <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-zinc-50 text-sm" data-testid={`calendar-usta-option-${u.id}`}>
                        <Checkbox checked={aUserIds.includes(u.id)} onCheckedChange={() => toggleUsta(u.id)} />
                        <span>{u.name || u.email}</span>
                      </label>
                    ))}
                  </div>
                )}
                {!isEdit && (
                  <p className="text-xs text-zinc-500 mt-1">
                    {aUserIds.length ? `${aUserIds.length} usta seçildi` : "Usta seçilmedi — görev genel (atanmamış) oluşturulur"}
                  </p>
                )}
              </div>

              <div className="col-span-2">
                <MultiDatePicker dates={aDates} setDates={setADates} testid="calendar-a-dates" />
              </div>
              <div className="col-span-2">
                <Label>Notlar</Label>
                <Textarea rows={2} value={aNotes} onChange={(e) => setANotes(e.target.value)} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="free" className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Başlık</Label>
                <Input value={fTitle} onChange={(e) => setFTitle(e.target.value)} placeholder="ör. Fuar hazırlığı" data-testid="calendar-free-title" />
              </div>
              <div>
                <Label>Usta (opsiyonel)</Label>
                <Select value={fUserId || "__none"} onValueChange={(v) => setFUserId(v === "__none" ? "" : v)}>
                  <SelectTrigger data-testid="calendar-free-user-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Genel (kimseye bağlı değil)</SelectItem>
                    {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Öncelik</Label>
                <Select value={fPriority} onValueChange={setFPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <MultiDatePicker dates={fDates} setDates={setFDates} testid="calendar-f-dates" />
              </div>
              <div className="col-span-2">
                <Label>Açıklama</Label>
                <Textarea rows={2} value={fDesc} onChange={(e) => setFDesc(e.target.value)} />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex items-center gap-2">
          {isEdit && (
            <Button type="button" onClick={del} variant="ghost" className="text-red-600 hover:text-red-700 mr-auto" data-testid="calendar-delete-button">
              <Trash2 size={14} className="mr-1" />Sil
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button
            onClick={tab === "assignment" ? saveAssignment : saveFree}
            disabled={saving}
            className="bg-zinc-900 hover:bg-zinc-800"
            data-testid="calendar-save-button"
          >
            {isEdit ? "Güncelle" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
